import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
  alpha,
  Card,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PersonAdd,
  AccountCircle,
  Verified,
  School,
  AttachMoney,
  ArrowForward,
  CheckCircle,
  Email,
  AccessTime,
  LocationOn,
  Person,
} from '@mui/icons-material';

function Tutors() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const benefits = [
    {
      icon: <AttachMoney sx={{ fontSize: 50 }} />,
      title: t('tutors.earnMoney'),
      description: t('tutors.earnMoneyDesc'),
    },
    {
      icon: <AccessTime sx={{ fontSize: 50 }} />,
      title: t('tutors.flexibleSchedule'),
      description: t('tutors.flexibleScheduleDesc'),
    },
    {
      icon: <LocationOn sx={{ fontSize: 50 }} />,
      title: t('tutors.workFromAnywhere'),
      description: t('tutors.workFromAnywhereDesc'),
    },
  ];

  const steps = [
    {
      icon: <PersonAdd sx={{ fontSize: 48 }} />,
      step: '01',
      title: t('tutors.step1Title'),
      desc: t('tutors.step1Desc'),
    },
    {
      icon: <AccountCircle sx={{ fontSize: 48 }} />,
      step: '02',
      title: t('tutors.step2Title'),
      desc: t('tutors.step2Desc'),
    },
    {
      icon: <Verified sx={{ fontSize: 48 }} />,
      step: '03',
      title: t('tutors.step3Title'),
      desc: t('tutors.step3Desc'),
    },
    {
      icon: <School sx={{ fontSize: 48 }} />,
      step: '04',
      title: t('tutors.step4Title'),
      desc: t('tutors.step4Desc'),
    },
    {
      icon: <AttachMoney sx={{ fontSize: 48 }} />,
      step: '05',
      title: t('tutors.step5Title'),
      desc: t('tutors.step5Desc'),
    },
  ];

  return (
    <Box sx={{ mt: '-44px' }}>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  color: 'white',
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                {t('tutors.heroTitle')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: alpha('#ffffff', 0.95),
                  mb: 4,
                  fontSize: { xs: '1.1rem', md: '1.4rem' },
                  lineHeight: 1.6,
                }}
              >
                {t('tutors.heroSubtitle')}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{
                  background: theme.palette.secondary.main,
                  color: theme.palette.primary.main,
                  textTransform: 'capitalize',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  '&:hover': {
                    background: alpha(theme.palette.secondary.main, 0.9),
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {t('tutors.getStarted')}
                <ArrowForward sx={{ ml: 1 }} />
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  overflow: 'hidden',
                  width: '100%',
                  height: { xs: '300px', md: '400px' },
                  position: 'relative',
                }}
              >
                <img
                  src="/tutor-detail.jpg"
                  alt="Tutor"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box id="benefits" sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 6,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            {t('tutors.whyChooseTitle')}
          </Typography>
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: 3,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 2,
                    }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                    }}
                  >
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ py: { xs: 6, md: 10 }, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 8,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            {t('tutors.howItWorksTitle')}
          </Typography>

          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: 3,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: theme.palette.primary.main,
                      '&::before': {
                        transform: 'scaleX(1)',
                      },
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontSize: '2.2rem',
                      fontWeight: 800,
                      color: alpha(theme.palette.primary.main, 0.08),
                      lineHeight: 1,
                    }}
                  >
                    {step.step}
                  </Box>
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2,
                      mt: 1,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1.5,
                      fontSize: { xs: '1.1rem', md: '1.2rem' },
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      lineHeight: 1.7,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {step.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <img
                  src="/TUTOR.png"
                  alt="Tutor"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 3,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                {t('tutors.flexibleTutoringTitle')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                }}
              >
                {t('tutors.flexibleTutoringDesc')}
              </Typography>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <CheckCircle sx={{ color: theme.palette.primary.main, fontSize: 30 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t('tutors.onlineTutoring')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <CheckCircle sx={{ color: theme.palette.primary.main, fontSize: 30 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t('tutors.inPersonTutoring')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <CheckCircle sx={{ color: theme.palette.primary.main, fontSize: 30 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t('tutors.setYourOwnRates')}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              {t('tutors.ctaTitle')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha('#ffffff', 0.9),
                mb: 4,
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              {t('tutors.ctaSubtitle')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{
                  background: theme.palette.secondary.main,
                  color: theme.palette.primary.main,
                  textTransform: 'capitalize',
                  px: 6,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  boxShadow: `0 8px 30px ${alpha(theme.palette.secondary.main, 0.4)}`,
                  '&:hover': {
                    background: alpha(theme.palette.secondary.main, 0.9),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.5)}`,
                  },
                }}
              >
                {t('tutors.signUpNow')}
                <ArrowForward sx={{ ml: 1 }} />
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/result')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  textTransform: 'capitalize',
                  px: 6,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  '&:hover': {
                    borderColor: 'white',
                    background: alpha('#ffffff', 0.1),
                  },
                }}
              >
                {t('tutors.browseTutors')}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Tutors;

